import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  IdCard,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import TenantUserListTable from "src/components/tables/TenantUserListTable";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantUserActions from "src/redux/slices/tenantUserSlice";
import * as tenantActions from "src/redux/slices/tenantSlice";
import * as tenantDepartmentActions from "src/redux/slices/tenantDepartmentSlice";
import { toast } from "react-toastify";
import usePlanUsage from "src/hooks/usePlanUsage";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Suspended", value: "suspended" },
];

const TeamManagementPage: React.FC = () => {
  const basePath = "/app/team-management";
  const navigate = useNavigate();
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<any | null>(null);

  const { tenantUserList, editTenantUser } = useAppSelector(
    (state) => state.tenantUser
  );
  const { tenantRoleList } = useAppSelector((state) => state.tenant);
  const { tenantDepartmentList } = useAppSelector(
    (state) => state.tenantDepartment
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = useMemo(
    () => user?.roleDetails?.permissions || user?.permissions || {},
    [user?.roleDetails?.permissions, user?.permissions]
  );
  const permissionEntries = Object.keys(permissions || {});
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canAccessTeamManagement =
    isTenantAdmin ||
    permissionEntries.length === 0 ||
    (permissions?.users || []).includes("view");

  const dispatch = useAppDispatch();
  const { planUsage } = usePlanUsage();

  const userLimit = planUsage?.plan?.features?.totalUsers;
  const userUsage = planUsage?.usage?.totalUsers;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isUserLimitReached =
    typeof userLimit === "number" &&
    typeof userUsage === "number" &&
    userUsage >= userLimit;
  const disableCreateUser = isPlanExpired || isUserLimitReached;

  const canCreateUser =
    isTenantAdmin || (permissions?.users || []).includes("create");
  const canEditUser =
    isTenantAdmin || (permissions?.users || []).includes("edit");
  const canToggleUser =
    isTenantAdmin || (permissions?.users || []).includes("delete");

  const getTenantUsers = useCallback(
    (page: number) => {
      dispatch(
        tenantUserActions.getTenantUserListRequest({
          page,
          limit,
          ...(search ? { search } : {}),
          ...(roleFilter ? { role: roleFilter } : {}),
          ...(departmentFilter ? { departmentId: departmentFilter } : {}),
          ...(statusFilter ? { status: statusFilter as "active" | "suspended" } : {}),
        })
      );
    },
    [dispatch, limit, search, roleFilter, departmentFilter, statusFilter]
  );

  useEffect(() => {
    dispatch(tenantActions.getTenantRoleListRequest({}));
    dispatch(
      tenantDepartmentActions.getTenantDepartmentListRequest({ page: 1, limit: 100 })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getTenantUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, limit, roleFilter, departmentFilter, statusFilter]);

  useEffect(() => {
    if (editTenantUser.data && statusUpdate) {
      setStatusUpdate(false);
      toast.success("Staff member updated successfully");
      dispatch(tenantUserActions.resetEditTenantUser());
      handleStatusModalClose();
      getTenantUsers(tenantUserList?.data?.pagination?.currentPage || 1);
    } else if (editTenantUser.error && statusUpdate) {
      toast.error(`Error: ${editTenantUser.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTenantUser, statusUpdate, dispatch]);

  const handleEdit = (data: any) => {
    navigate(`${basePath}/user/${data._id}/edit`, {
      state: {
        user: {
          ...data,
          role: data?.role?._id || data?.role,
        },
      },
    });
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    dispatch(
      tenantUserActions.editTenantUserRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "suspended" : "active",
      })
    );
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setRoleFilter("");
    setDepartmentFilter("");
    setStatusFilter("");
  };
  const hasActiveFilters =
    !!search || !!roleFilter || !!departmentFilter || !!statusFilter;

  const handlePageChange = (page: number) => getTenantUsers(page);

  if (!canAccessTeamManagement) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <DashboardLayout
      meta={{
        title: "Staff | Invyntra",
        description: "Manage staff for your workspace.",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <IdCard size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Staff
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Employee records and optional login accounts.
              </p>
            </div>
          </div>

          {canCreateUser && (
            <Button
              rounded="full"
              onClick={() => navigate(`${basePath}/user/create`)}
              disabled={disableCreateUser}
              title={
                isPlanExpired
                  ? "Plan expired. Please upgrade."
                  : isUserLimitReached
                  ? "User limit reached. Please upgrade."
                  : undefined
              }
            >
              <Plus size={16} className="mr-1" />
              {disableCreateUser ? "Limit Reached" : "Add Staff"}
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search staff..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Designation"
                value={roleFilter}
                onChange={setRoleFilter}
                allLabel="All designations"
                options={(tenantRoleList?.data?.data || []).map((r: any) => ({
                  label: r.name,
                  value: r._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Department"
                value={departmentFilter}
                onChange={setDepartmentFilter}
                allLabel="All departments"
                options={(tenantDepartmentList?.data?.data || []).map((d: any) => ({
                  label: d.name,
                  value: d._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Staff"
            description="Search and manage your staff."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TenantUserListTable
            data={tenantUserList?.data?.data || []}
            pagination={tenantUserList?.data?.pagination}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onToggleStatus={(row) => {
              setStatusRow(row);
              setShowStatusModal(true);
            }}
            canEdit={canEditUser}
            canToggle={canToggleUser}
            loading={tenantUserList.loading}
          />
        </Card>
      </div>

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "suspend" : "activate"
          } this staff member?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editTenantUser.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default TeamManagementPage;
